import { useState, useEffect } from 'react';
import { Download, Trash2, FileText, Calendar, Layers, Home, ChevronRight, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config/api.config';
import { useAuthStore } from '../store/authStore';
import { confirmDelete, showDeleteSuccess, showDeleteError } from '@/utils/deleteConfirmation';

interface GeneratedSlide {
  _id: string;
  fileName: string;
  originalFileName: string;
  slidesPerPage: 2 | 4;
  totalSlides: number;
  fileSize: number;
  generatedAt: string;
}

export default function GeneratedSlidesList() {
  const { user } = useAuthStore();
  const userPermissions = user?.permissions || [];
  const canDelete = userPermissions.includes('slides_history_delete');

  const [slides, setSlides] = useState<GeneratedSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/slides/list`);
      const data = await response.json();

      if (data.success) {
        setSlides(data.data);
      }
    } catch (error) {
      console.error('Error fetching slides:', error);
      Swal.fire('Error', 'Failed to load generated slides', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (slideId: string) => {
    // Open PDF in new tab for inline viewing (not download)
    const viewUrl = `${API_BASE_URL}/slides/view/${slideId}`;
    window.open(viewUrl, '_blank');
  };

  const handleDownload = async (slideId: string, fileName: string) => {
    try {
      setDownloadingId(slideId);

      const response = await fetch(`${API_BASE_URL}/slides/download/${slideId}`);

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      Swal.fire('Success', 'Slide downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error downloading slide:', error);
      Swal.fire('Error', 'Failed to download slide', 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (slideId: string, fileName: string) => {
    const confirmed = await confirmDelete({
      title: 'Delete Slide?',
      itemName: fileName,
    });

    if (confirmed) {
      try {
        const response = await fetch(`${API_BASE_URL}/slides/${slideId}`, {
          method: 'DELETE'
        });

        const data = await response.json();

        if (data.success) {
          showDeleteSuccess('Slide has been deleted successfully.');
          fetchSlides(); // Refresh list
        } else {
          throw new Error(data.error);
        }
      } catch (error) {
        console.error('Error deleting slide:', error);
        showDeleteError('Failed to delete slide');
      }
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading generated slides...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/dashboard" className="flex items-center hover:text-blue-600 transition-colors">
          <Home className="w-4 h-4" />
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-400">Slides</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-semibold">Slides History</span>
      </nav>

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <FileText className="w-8 h-8" />
          Generated Slides History
        </h1>
        <p className="mt-2 text-gray-600">
          Download or manage your previously generated PDF slides
        </p>
      </div>

      {/* Stats */}
      {slides.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-semibold">Total Slides</p>
                <p className="text-2xl font-bold text-blue-900 mt-1">{slides.length}</p>
              </div>
              <FileText className="w-12 h-12 text-blue-600 opacity-50" />
            </div>
          </div>
          <div className="bg-orange-50 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-semibold">Total Size</p>
                <p className="text-2xl font-bold text-orange-900 mt-1">
                  {formatFileSize(slides.reduce((sum, slide) => sum + slide.fileSize, 0))}
                </p>
              </div>
              <Layers className="w-12 h-12 text-orange-600 opacity-50" />
            </div>
          </div>
          <div className="bg-green-50 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-semibold">Latest</p>
                <p className="text-sm font-bold text-green-900 mt-1">
                  {formatDate(slides[0].generatedAt)}
                </p>
              </div>
              <Calendar className="w-12 h-12 text-green-600 opacity-50" />
            </div>
          </div>
        </div>
      )}

      {/* Slides List */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        {slides.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No slides generated yet</p>
            <p className="text-gray-400 text-sm mt-2">Generate your first slide from the Slides Maker page</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">File Name</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Slides/Page</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Total Slides</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Size</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Generated At</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {slides.map((slide) => (
                  <tr key={slide._id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-gray-900">{slide.originalFileName}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <Layers className="w-4 h-4 text-orange-600" />
                        <span className="text-gray-700">{slide.slidesPerPage}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-gray-700">{slide.totalSlides}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-gray-600 text-sm">{formatFileSize(slide.fileSize)}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600 text-sm">{formatDate(slide.generatedAt)}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleView(slide._id)}
                          className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
                          title="View PDF"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDownload(slide._id, slide.originalFileName.replace('.docx', `_${slide.slidesPerPage}_slides.pdf`))}
                          disabled={downloadingId === slide._id}
                          className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors disabled:opacity-50"
                          title="Download"
                        >
                          {downloadingId === slide._id ? (
                            <div className="w-5 h-5 border-2 border-blue-700 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Download className="w-5 h-5" />
                          )}
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(slide._id, slide.originalFileName)}
                            className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
